import React, { useContext, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { App, Modal, Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { postCoreEvent } from '../../../../services/core-event'
import { DefaultRoleIdEnum, IRoleAssignment, RoleScopeTypeEnum } from '@vacationtracker/shared/types/roles'
import { EventTypeEnum } from '@vacationtracker/shared/types/core-event'
import { IUserUpdatedEvent } from '@vacationtracker/shared/types/user'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'

import * as logger from '../../../../services/logger'
import { showApiErrorNotification } from '../showApiErrorNotification'
import UserUpdateForm from '../../../../components/user-update-form'
import { graphqlRequest } from '../../../../services/tanstack-query'
import { getUserRoleAssignments } from '../../../../graphql/custom-queries'
import { notificationStore } from '../../../../context/notificationsContext/store'
import { useAppSelector } from '../../../../store/hooks'
import { selectAuthCompanySlice } from '../../../../store/auth-company-slice'
import { IGetTeamsShort } from '../../../../types/teams'
import { IGetLocationsShort } from '../../../../types/locations'
import { IUserFilteredInfo } from '../../../../types/users'
import { IUserProps } from '../../../../components/user-profile-tab/types'

interface IGetUserRoleAssignments {
  getUser: { id: string; roleAssignments?: IRoleAssignment[] }
}

export interface IEditUserModalProps {
  user: IUserFilteredInfo
  teams: IGetTeamsShort[]
  locations: IGetLocationsShort[]
  isLocationAdmin: boolean
  onClose: () => void
  fetchLocationDeleteSuggestion?: (params: { locationId?: string; affectedUserWasActive: boolean }) => Promise<{ locationId: string; locationName: string } | null>
  onSuggestLocationDelete?: (params: { locationId: string; locationName: string }) => void
}

export const EditUserModal = (props: IEditUserModalProps): React.ReactElement => {
  const { user, teams, locations, isLocationAdmin, onClose, fetchLocationDeleteSuggestion, onSuggestLocationDelete } = props
  const { formatMessage } = useIntl()
  const { notification } = App.useApp()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { addActionNotifications } = useContext(notificationStore)

  // The list row carries everything the form edits except role-assignment scopes, which live in a
  // separate view table - fetch them only when the edited user actually is a location admin.
  const needsRoleAssignments = Boolean(user.roles?.includes(DefaultRoleIdEnum.LOCATION_ADMIN))
  const [roleAssignments, setRoleAssignments] = useState<IRoleAssignment[] | null>(needsRoleAssignments ? null : [])

  useEffect(() => {
    if (!needsRoleAssignments) {
      return
    }
    let cancelled = false
    graphqlRequest<IGetUserRoleAssignments>(getUserRoleAssignments, { id: user.id })
      .then((response) => {
        if (!cancelled) {
          setRoleAssignments(response.getUser.roleAssignments ?? [])
        }
      })
      .catch((error) => {
        logger.warning(error)
        if (!cancelled) {
          notification.error({ message: formatMessage({ id: 'error.generic' }) })
          onClose()
        }
      })
    return () => {
      cancelled = true
    }
  }, [user.id, needsRoleAssignments])

  if (!roleAssignments) {
    return (
      <Modal open footer={null} closable={false}>
        <Spin />
      </Modal>
    )
  }

  const postEvent = (body: object) => postCoreEvent(body, 'users-table')

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSave = async (formData: any) => {
    try {
      const correlationIds: string[] = []

      if (formData.changeTeam && formData.teamId !== user.team.id) {
        const response = await postEvent({
          eventType: 'TEAM_USER_MOVED',
          eventGroup: 'USER',
          teamId: formData.teamId,
          userId: user.id,
        })
        correlationIds.push(response.correlationId as string)
        const teamName = teams.find((team) => team.id === formData.teamId)?.name
        notification.open({
          key: response.correlationId,
          message: formatMessage({ id: 'user.team.moveInProgress' }, { name: teamName }),
          icon: <LoadingOutlined />,
          duration: 0,
        })
      }

      let locationDeleteSuggestion: Promise<{ locationId: string; locationName: string } | null> = Promise.resolve(null)
      if (formData.changeLocation && formData.locationId !== user.location.id) {
        // Kicked off before the move: the suggestion needs the pre-mutation active count
        locationDeleteSuggestion =
          fetchLocationDeleteSuggestion?.({
            locationId: user.location.id,
            affectedUserWasActive: user.status === 'ACTIVE',
          }) ?? Promise.resolve(null)
        const response = await postEvent({
          eventType: 'LOCATION_USER_MOVED',
          eventGroup: 'USER',
          locationId: formData.locationId,
          userId: user.id,
        })
        correlationIds.push(response.correlationId as string)
        const locationName = locations.find((location) => location.id === formData.locationId)?.name
        notification.open({
          key: response.correlationId,
          message: formatMessage({ id: 'user.location.moveInProgress' }, { name: locationName }),
          icon: <LoadingOutlined />,
          duration: 0,
        })
      }

      const wasLocationAdmin = roleAssignments.some((assignment) => assignment.roleId === DefaultRoleIdEnum.LOCATION_ADMIN)
      const isNowLocationAdmin = formData.role === DefaultRoleIdEnum.LOCATION_ADMIN
      const previousLocationIds = roleAssignments.find((assignment) => assignment.roleId === DefaultRoleIdEnum.LOCATION_ADMIN)?.scope?.resourceIds ?? []
      const newLocationIds = formData.managedLocationIds ?? []
      const locationIdsChanged = JSON.stringify([...previousLocationIds].sort()) !== JSON.stringify([...newLocationIds].sort())

      if ((!wasLocationAdmin && isNowLocationAdmin) || (wasLocationAdmin && isNowLocationAdmin && locationIdsChanged)) {
        await postEvent({
          eventType: EventTypeEnum.USER_ROLE_ASSIGNED,
          eventGroup: 'USER',
          userId: user.id,
          roleId: DefaultRoleIdEnum.LOCATION_ADMIN,
          scopeType: RoleScopeTypeEnum.LOCATION,
          resourceIds: newLocationIds,
        })
      } else if (wasLocationAdmin && !isNowLocationAdmin) {
        await postEvent({
          eventType: EventTypeEnum.USER_ROLE_UNASSIGNED,
          eventGroup: 'USER',
          userId: user.id,
          roleId: DefaultRoleIdEnum.LOCATION_ADMIN,
        })
      }

      const event: IUserUpdatedEvent = {
        eventType: 'USER_UPDATED',
        eventGroup: 'USER',
        userId: user.id,
        name: formData.name,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        platform: user.platform,
        startDate: formData.startDate,
        endDate: formData.endDate,
        employeeId: formData.employeeId,
        isAdmin: formData.isAdmin,
        changeEmail: formData.changeEmail ?? false,
        isNameLocked: user.isNameLocked ? true : formData.changeName,
        imageUrl: formData.imageUrl ?? user.imageUrl ?? undefined,
        birthday: formData.birthday,
        ...(formData.changeStatus && formData.status !== user.status && { status: formData.status }),
      }

      const apiResponse = await postEvent(event)
      correlationIds.push(apiResponse.correlationId as string)
      notification.open({
        key: apiResponse.correlationId,
        message: formatMessage({ id: 'user.updateInProgress' }),
        icon: <LoadingOutlined />,
        duration: 0,
      })

      addActionNotifications(correlationIds)
      onClose()
      const suggestion = await locationDeleteSuggestion
      if (suggestion) {
        onSuggestLocationDelete?.(suggestion)
      }
    } catch (error) {
      logger.warning({ errorType: 'ERROR EDIT USER', error })
      showApiErrorNotification(notification, formatMessage, error)
    }
  }

  const formUser: IUserProps = {
    id: user.id,
    name: user.name,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    isAdmin: Boolean(user.isAdmin),
    team: { id: user.team.id, name: user.team.name, approvers: [] },
    location: user.location as IUserProps['location'],
    platform: user.platform,
    startDate: user.startDate,
    endDate: user.endDate,
    employeeId: user.employeeId,
    role: user.role,
    status: user.status,
    isNameLocked: user.isNameLocked,
    approvers: [],
    workWeekType: '',
    workWeekInfo: null,
    imageUrl: user.imageUrl,
    birthday: user.birthday,
  }

  return (
    <UserUpdateForm
      initialRolloverChanged={false}
      visible={true}
      user={formUser}
      roleAssignments={roleAssignments}
      teams={teams}
      locations={locations}
      isLocationAdmin={isLocationAdmin}
      handleCancel={onClose}
      onSave={handleSave}
      enableUserEndDate={Boolean(authCompany?.userEndDate)}
      companyPlan={authCompany?.plan as SubscriptionPlanEnum}
    />
  )
}
